import AxiosService from '@/api/ApiBase';
import BaseReference from '@/types/base/base.reference';
import { CfsLocationShipmentDirection } from '@/types/cfs-location';
import {
  ContainerTypeReference,
  VesselDataReference,
} from '@/types/base/reference/reference.types';
import { VesselReference, VesselTracking } from '@/types/reference/vessel.reference';

const REFERENCE_BASE_URL = 'reference';
const FORCE_DATA_BASE_URL = 'warehouse/forceData';
const UNIT_TYPE_BASE_URL = 'common/references';

class ReferencesService {
  getContainerTypes() {
    return AxiosService.get<ContainerTypeReference[]>(`${UNIT_TYPE_BASE_URL}/unittypes`, {
      params: { deleted: false },
    });
  }
  getAirContainerTypes() {
    return AxiosService.get<ContainerTypeReference[]>(`${UNIT_TYPE_BASE_URL}/uldtypes`);
  }

  getAirports(search?: string, country_code: string = '') {
    return AxiosService.get<any>(`${REFERENCE_BASE_URL}/airports`, {
      params: {
        country_code,
        search,
      },
    });
  }

  getPackageTypes() {
    return AxiosService.get<any>(`${REFERENCE_BASE_URL}/package/type`);
  }

  getAirPackageTypes() {
    return AxiosService.get<any>(`${UNIT_TYPE_BASE_URL}/packagetypes`, {
      params: {
        deleted: false,
      },
    });
  }

  getMeasureUnits() {
    return AxiosService.get<any>(`${FORCE_DATA_BASE_URL}/measureUnits`);
  }

  getWeightUnits() {
    return AxiosService.get<any>(`${FORCE_DATA_BASE_URL}/weightUnits`);
  }

  getPickupCarriers() {
    return AxiosService.get<any>(`${FORCE_DATA_BASE_URL}/pickupCarriers`);
  }

  getDivisions() {
    return AxiosService.get<any>(`${FORCE_DATA_BASE_URL}/divisions`);
  }

  getPaymentLookups() {
    return AxiosService.get<any>(`${FORCE_DATA_BASE_URL}/paymentLookups`);
  }

  getServices() {
    return AxiosService.get<BaseReference[]>(`${FORCE_DATA_BASE_URL}/services`);
  }

  getWarehouses() {
    return AxiosService.get<any>(`${FORCE_DATA_BASE_URL}/warehouses`);
  }

  getEntryTypes() {
    return AxiosService.get<BaseReference[]>(`${FORCE_DATA_BASE_URL}/inbond/entry-types`);
  }

  getLoadTypes() {
    return AxiosService.get<any>(`${FORCE_DATA_BASE_URL}/onhand/loadTypes`);
  }

  getTransportModes(shipmentDirection: CfsLocationShipmentDirection = 'I') {
    return AxiosService.get<BaseReference[]>(`${FORCE_DATA_BASE_URL}/modesOfTransportation`, {
      params: {
        shipmentDirection,
      },
    });
  }

  PlaceOfDelivery(search: string) {
    return AxiosService.get<BaseReference[]>(`${FORCE_DATA_BASE_URL}/placesOfDelivery`, {
      params: {
        q: search,
      },
    });
  }

  getEmptyReturnLocations() {
    return AxiosService.get<any>(`${FORCE_DATA_BASE_URL}/emptyReturnLocations`);
  }

  async getPlacesByLocCode(code: string) {
    const { data } = await AxiosService.get<
      {
        locCode: string;
        name: string;
      }[]
    >(`${REFERENCE_BASE_URL}/places/loc_code/${code}`);

    return data.map((item) => ({
      code: item.locCode,
      description: `${item.name} - ${item.locCode}`,
    }));
  }

  async getPorts(search: string) {
    const { data } = await AxiosService.get<BaseReference[]>(`${REFERENCE_BASE_URL}/ports`, {
      params: {
        q: search,
      },
    });
    return (
      data?.map((city: any) => ({
        code: city.locCode,
        description: `${city.name} - ${city.locCode}`,
        region: city.region?.name || null,
        locCode: city.locCode,
        country: city.country.name,
      })) || []
    );
  }

  async getDefaultPorts() {
    return await AxiosService.get<BaseReference[]>(`${FORCE_DATA_BASE_URL}/defaultPorts`);
  }

  getPackageGroup() {
    return AxiosService.get<any>(`${REFERENCE_BASE_URL}/package/type`);
  }

  getPackageType() {
    return AxiosService.get<any>(`${REFERENCE_BASE_URL}/package/type`);
  }

  getTimeZones() {
    return AxiosService.get<BaseReference[]>(`user/reference/timezones`);
  }

  getFacilities(query: string) {
    return AxiosService.get<BaseReference[]>(`customs/us/abi/references/facilities`, {
      params: {
        q: query,
        status: 'A',
      },
    });
  }

  searchVessels(search: string) {
    return AxiosService.get<VesselReference[]>(`${REFERENCE_BASE_URL}/vessels/search/${search}`);
  }

  async trackVessel(imo: string) {
    const { data } = await AxiosService.get<VesselTracking>(`/track/vessel/imo/${imo}`);
    return data;
  }

  async getVesselData(fromPort: string, toPort: string) {
    const { data } = await AxiosService.get<VesselDataReference>(
      `/reference/port/route/${fromPort}/${toPort}`
    );
    return data;
  }

  async getInventoryLocations(cfsId?: string) {
    const { data } = await AxiosService.post<any>('warehouse/v2/inventory/locations/search', {
      cfsId,
      chunkSize: 9999,
    });
    return data;
  }
}

export default new ReferencesService();
